import { CustomButton } from "@/components/ui/Buttons";
import MultiSelect from "@/components/ui/MultiSelect";
import RadioButton from "@/components/ui/RadioButton";
import { videoShootingPropsOptions } from "@/constant";
import EditConditionalField from "@/pages/authentication/signUp/components/EditConditionalField";
import { useUpdateCreatorMutation } from "@/redux/feature/user/userApi";
import { resetUserChanges, updateUser } from "@/redux/feature/user/userSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { SelectComponetOption, TUser } from "@/types/types";
import { handleApiRequest } from "@/utils/handleApiRequest";
import { X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

interface EditProjectDetailsModalProps {
  data: TUser;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

/* ── Reusable section divider — same orange pill style ── */
const SectionDivider = ({ title }: { title: string }) => (
  <div className="flex items-center gap-x-2">
    <div className="bg-[#FF6600] px-[8.5px] rounded-[20px] shrink-0">
      <p className="text-white text-[11px] leading-5 tracking-normal">
        {title}
      </p>
    </div>
    <div className="flex-1 h-px bg-[#F2F4F7]" />
  </div>
);

/* ── Yes / No radio block ── */
const YesNoField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (val: boolean) => void;
}) => (
  <div>
    <p className="text-[#121217] text-sm font-medium tracking-[-0.3px] mb-2">
      {label}
    </p>
    <div className="bg-[#FAFAFA] py-3 px-2 rounded-lg flex gap-x-6">
      {[
        { label: "Yes", val: true },
        { label: "No", val: false },
      ].map((opt) => (
        <label
          key={opt.label}
          className="flex items-center gap-1.5 cursor-pointer"
        >
          <RadioButton
            onChange={() => onChange(opt.val)}
            checked={value === opt.val}
          />
          <span className="text-xs font-medium text-[#475467]">
            {opt.label}
          </span>
        </label>
      ))}
    </div>
  </div>
);

const EditProjectDetailsModal = ({
  data,
  onClose,
}: EditProjectDetailsModalProps) => {
  const dispatch = useAppDispatch();
  const userSliceData = useAppSelector((state) => state.user.user);
  const [updateCreator] = useUpdateCreatorMutation();

  const handlerUpdateCreator = async () => {
    if (!data || !data.user_id) {
      toast.error("User information is missing");
      return;
    }

    const payload = {
      id: data.user_id,
      body: {
        hasRelativesInVideo: userSliceData.hasRelativesInVideo || false,
        props: userSliceData.props || "",
        greenScreen: userSliceData.greenScreen || false,
        noiselessRoomAndMicrophoneSpecs:
          userSliceData.noiselessRoomAndMicrophoneSpecs || {},
        cameraOrSmartphoneSpecs: userSliceData.cameraOrSmartphoneSpecs || {},
        lightingSpecs: userSliceData.lightingSpecs || {},
        additionalInfo: userSliceData.additionalInfo || {},
      },
    };

    await handleApiRequest({
      payload,
      requestFunction: updateCreator,
      onSuccess: () => {
        toast.success("Project details updated successfully!");
        onClose(false);
      },
      onError: (err) => {
        const error = err as {
          data?: { errorSource?: { path?: string; message: string }[] };
        };
        if (error?.data?.errorSource?.length) {
          error.data.errorSource.forEach((item) => {
            toast.error(
              item.path ? `${item.path} is ${item.message}` : item.message,
            );
          });
        } else {
          toast.error("Failed to update project details. Please try again.");
        }
      },
    });
  };

  const handlerCancel = () => {
    dispatch(resetUserChanges(data));
    onClose(false);
  };

  return (
    <section className="bg-white rounded-2xl flex flex-col max-h-[90vh]">
      {/* ── Header ── */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-[#F2F4F7] shrink-0">
        <div className="flex items-center gap-x-2">
          <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
            <p className="text-white text-[11px] leading-5 tracking-normal">
              Edit
            </p>
          </div>
          <h1 className="text-[#060B13] text-xl tracking-[-0.7px] font-medium">
            Project Details
          </h1>
        </div>
        <button
          onClick={handlerCancel}
          className="text-[#98A2B3] hover:text-[#060B13] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5">
        {/* ── Appearance ── */}
        <SectionDivider title="Appearance" />

        <YesNoField
          label="Do you have peers or relatives joining you in your video?"
          value={userSliceData?.hasRelativesInVideo === true}
          onChange={(val) =>
            dispatch(updateUser({ field: "hasRelativesInVideo", value: val }))
          }
        />

        {/* Props */}
        <div>
          <p className="text-[#121217] text-sm font-medium tracking-[-0.3px] mb-1">
            Availability of Props to use in shoot
          </p>
          <p className="text-[#475467] text-[10px] tracking-normal mt-[2px] mb-3">
            Select all props you have access to.
          </p>
          <SelectProps defaultValue={userSliceData?.props[0]} />
        </div>

        {/* ── Equipment ── */}
        <SectionDivider title="Equipment" />

        <YesNoField
          label="Do you have a green screen or aesthetic background at your place?"
          value={userSliceData?.greenScreen === true}
          onChange={(val) =>
            dispatch(updateUser({ field: "greenScreen", value: val }))
          }
        />

        <EditConditionalField
          fieldName="noiselessRoomAndMicrophoneSpecs"
          hasPropertyName="hasAudioEquipment"
          question="Are you equipped with a noiseless room and microphone? Can mention the specs."
          textareaLabel="Please provide details about your audio setup"
          textareaPlaceholder="Describe your audio equipment..."
        />

        <EditConditionalField
          fieldName="cameraOrSmartphoneSpecs"
          hasPropertyName="hasCamera"
          question="Are you equipped with a high-definition Camera/Smartphone with a tripod? Kindly mention the specifications."
          textareaLabel="Kindly mention the camera specifications"
          textareaPlaceholder="Please describe..."
        />

        <EditConditionalField
          fieldName="lightingSpecs"
          hasPropertyName="hasLighting"
          question="Are you equipped with artificial lighting for recording videos? Kindly mention the specifications."
          textareaLabel="Kindly mention the lighting specifications"
          textareaPlaceholder="Please describe..."
        />

        {/* ── Additional Info ── */}
        <SectionDivider title="Additional Info" />

        <EditConditionalField
          fieldName="additionalInfo"
          hasPropertyName="hasAdditionalExperience"
          question="Is there anything else you'd like us to know about you or your experience as an anchor/speaker?"
          textareaLabel="Kindly mention your experience"
          textareaPlaceholder="Please describe..."
        />
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#F2F4F7] shrink-0">
        <CustomButton
          variant="outline"
          border="1px solid #E4E4E4"
          fontSize="14px"
          fontWeight="600"
          color="#4D4D4D"
          padding="5px 10.5px"
          borderRadius="200px"
          className="tracking-[-0.11px]"
          onClick={handlerCancel}
        >
          Cancel
        </CustomButton>

        <CustomButton
          backgroundColor="#FF6600"
          fontSize="14px"
          fontWeight="600"
          color="white"
          padding="5px 10.5px"
          borderRadius="200px"
          className="tracking-[-0.11px]"
          onClick={handlerUpdateCreator}
        >
          Save Changes
        </CustomButton>
      </div>
    </section>
  );
};

export default EditProjectDetailsModal;

/* ── SelectProps sub-component ── */
const SelectProps = ({ defaultValue }: { defaultValue?: string }) => {
  const dispatch = useAppDispatch();

  const defaultSelectedProps = defaultValue?.split(", ").filter(Boolean);
  const defaultSelectedItems = videoShootingPropsOptions.filter((item) =>
    defaultSelectedProps?.includes(item.id),
  );
  const [selectedProps, setSelectedProps] =
    useState<SelectComponetOption[]>(defaultSelectedItems);

  const handleSelectProps = (options: SelectComponetOption[] | null) => {
    if (options) {
      setSelectedProps(options);
      dispatch(
        updateUser({
          field: "props",
          value: options.map((option) => option.id).join(", "),
        }),
      );
    } else {
      setSelectedProps([]);
      dispatch(updateUser({ field: "props", value: "" }));
    }
  };

  return (
    <MultiSelect
      label=""
      options={videoShootingPropsOptions}
      selectedOptions={selectedProps}
      onSelect={handleSelectProps}
      placeholder="Select Options..."
      showOtherInputField={true}
    />
  );
};
